/**
 * ── THE DOME'S TWO SIZE FACTS, IN A MODULE A NODE BUILD STEP CAN IMPORT (B2, again) ─────────────
 *
 * `CAMERA_FAR_M`, `SKY_SCALE_MAX_M` and {@link bakeFarForSky} lived in `sky.ts` and are re-exported
 * from there unchanged. They moved here for **exactly** the reason `sunAngles.ts` moved, and that
 * file's header is the precedent this one is following rather than a new argument:
 *
 *   `sky.ts` value-imports `three/webgpu` AND spells its own relative imports without a `.ts`
 *   extension (a bundler-only convention). A Node tool that wants one of these three values gets
 *   `ERR_MODULE_NOT_FOUND` on `./sunAngles` before it ever reaches the renderer — MEASURED, on this
 *   worktree, while making the traced baker resolve its own lighting.
 *
 * The tool that needs them is `tools/bake/worldLighting.ts`: the traced producer resolves the
 * atlas header's whole `lighting` block from `world.json` instead of reading it off a rasterised
 * atlas, and `sky.scaleM` in that block is the CLAMPED scale, not the authored one. A second copy
 * of `Math.min(c.scaleM, SKY_SCALE_MAX_M)` in `tools/` would be a second answer to "how big is this
 * dome" — the failure the angle vocabulary was invented to prevent, one field over.
 *
 * ⚠️ IT MUST STAY IMPORT-FREE AND EXTENSION-EXPLICIT, for the reason above. Nothing on this chain
 * may reach `three`, and every relative import here must carry its `.ts`.
 */

/**
 * The main camera's far plane, in metres. The dome's ceiling is derived from it, and every consumer
 * that has to know how far the world extends reads it here.
 */
export const CAMERA_FAR_M = 400;

/**
 * The largest `sky.scaleM` that renders without clipped corners. The dome is a `BoxGeometry` scaled
 * uniformly, so its most distant point from a camera at the centre is the corner at `scale · √3/2`;
 * beyond `CAMERA_FAR_M` the corners are clipped and the sky grows holes. (The camera is never
 * exactly at the centre, which is why worlds pick a scale with margin.)
 */
export const SKY_SCALE_MAX_M = (2 * CAMERA_FAR_M) / Math.sqrt(3);

/**
 * The `far` a probe bake must use for its `CubeCamera` so that every probe in the volume can see
 * the dome. The vendored v1 bake options defaulted this to 100 m — a sky at any useful scale is
 * behind that, and the bake then reports complete success having photographed nothing.
 * `CaptureBakeOptions.far` is REQUIRED rather than defaulted so the trap is unrepresentable.
 *
 * Derived, not guessed: worst case is a probe at one corner of the volume looking at the opposite
 * corner of the dome, so `dome corner distance + volume half-diagonal`, plus margin. The cost of a
 * larger far plane on a 32² cube face is depth precision, and the whole scene lives inside a few
 * hundred metres.
 */
export function bakeFarForSky(skyScaleM: number, volumeHalfDiagonalM: number): number {
  return Math.ceil((skyScaleM * Math.sqrt(3)) / 2 + volumeHalfDiagonalM + 50);
}

/**
 * ⭐ THE FAR PLANE FOR A WORLD WITH **NO DOME**, and it is a named constant rather than a literal
 * because two producers now have to agree on it.
 *
 * `scene.ts` writes `sky ? bakeFarForSky(sky.info().scaleM, halfDiagonal) : 100` — the 100 is the
 * no-dome arm, and it is the number a bake's cube cameras clip at when nothing distant exists to
 * photograph. The traced producer has to reach the same answer from `world.json` alone, and a bare
 * `100` in `tools/` would be the second copy this module exists to prevent.
 */
export const BAKE_FAR_NO_SKY_M = 100;
